import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Editor } from "primereact/editor";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import Quill from "quill";
import QuillTableBetter from "quill-table-better";
import "quill-table-better/dist/quill-table-better.css";
import BlotFormatter2 from "@enzedonline/quill-blot-formatter2";
import "@enzedonline/quill-blot-formatter2/dist/css/quill-blot-formatter2.css";
import styles from "./RichTextEditorField.module.css";

/**
 * ASTT-7167/ASTT-7176/ASTT-7294 — shared rich-text field for LearnPoint DTR / Hand-Off Report.
 * Single source of truth for both TrainingNotes.js and HandOffReport.js.
 *
 * Table persistence fix: quill-table-better breaks when PrimeReact loads content via
 * setContents on mount; handleEditorLoad reloads via updateContents instead (isReloadingRef
 * stops that reload from bubbling to parent state).
 *
 * Inline image paste/insert: images are inserted as local blob: previews immediately (no
 * upload here), tagged with a placeholder token, and handed to the parent via
 * onImagePending to upload on Save. Outgoing html swaps blob: for pending:{token} so a
 * session-only blob URL is never what's saved. DTRSanitizedImage (below) is required for any
 * of this to render at all — see its comment.
 *
 * ASTT-7294 follow-up (robustness pass) fixed three separate bugs here:
 *   1. Toolbar image button: tagging happened AFTER insertEmbed, one text-change event too
 *      late (insertEmbed fires text-change synchronously) — raw blob: URLs leaked into saved
 *      content instead of pending:{token} markers. Fixed by queueing before insertEmbed, same
 *      as the paste path already did (see uploadHandlerRef).
 *   2. Paste: only handled clipboardData.files; many paste sources (web page "copy image",
 *      some Office paths) only ever provide a data: URI, never a File — those images rendered
 *      but were silently never uploaded. Fixed with a data: URI decode fallback in the img
 *      matcher (see handleEditorLoad).
 *   3. Maximize/restore: each transition mounts a brand-new <Editor> (Dialog portals to
 *      document.body), seeded from frozenEditorValueRef — which is deliberately NOT kept in
 *      sync with every keystroke (see its own comment). Without an explicit resync at the
 *      transition boundary, anything typed/pasted since activation was invisible to the new
 *      mount. Fixed in handleMaximizeClick/handleRestore.
 */

// Registers quill-table-better once, guarded against double-registration.
if (!Quill.imports["modules/table-better"]) {
  Quill.register({ "modules/table-better": QuillTableBetter }, true);
}

// Registers quill-blot-formatter2 (image resize/align/delete toolbar) once.
if (!Quill.imports["modules/blotFormatter2"]) {
  Quill.register("modules/blotFormatter2", BlotFormatter2, true);
}

// Quill's built-in Image format only allows http/https/data URL schemes (quill/formats/
// image.js sanitize()) — blob: was silently rejected, which is why every pasted/inserted
// image showed a broken glyph regardless of insertion method. This subclass allows blob:
// through while keeping Quill's original sanitize behavior for everything else.
const QuillImage = Quill.import("formats/image");
class DTRSanitizedImage extends QuillImage {
  static sanitize(url) {
    if (typeof url === "string" && url.startsWith("blob:")) {
      return url;
    }
    return super.sanitize(url);
  }
}
Quill.register(DTRSanitizedImage, true);

// Quill's Delta class, needed by the img matcher below (HTML-fallback paste path).
const QuillDelta = Quill.import("delta");

// ASTT-7176: inline pasted image constraints. Server-side (TrackerLearnPointService)
// is the authoritative gate on both the size cap and the type allowlist — these are
// UX-only pre-checks to skip an obviously-doomed upload call.
const MAX_INLINE_IMAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
const ALLOWED_INLINE_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/gif"];

// A distinctive, effectively-unique placeholder inserted at paste/insert time and searched
// for by content once resolved (not by index, since the user may keep typing meanwhile).
const makePlaceholderToken = () =>
  `\uFFFC uploading-image-${Date.now()}-${Math.random().toString(36).slice(2)} \uFFFC`;

// Renames pasted images to a unique filename (browsers give every clipboard image the same
// generic name, which the upload API would reject as a duplicate on the second paste).
const MIME_TO_EXTENSION = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
};

const makeUniqueImageFile = (file) => {
  const extension = MIME_TO_EXTENSION[file.type] || "png";
  const uniqueName = `pasted-image-${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;
  return new File([file], uniqueName, { type: file.type });
};

// Swaps a pending image's live blob: src for its pending:{token} marker in the OUTGOING html
// (parent state must never hold a session-only blob: URL). Queries the live quill DOM
// directly rather than re-parsing the html string, since a custom data-pending-token
// attribute isn't guaranteed to survive Quill's own HTML serialization.
const transformPendingImageMarkers = (html, quill) => {
  if (!html || !quill) return html;
  const pendingImages = quill.root.querySelectorAll("img[data-pending-token]");
  if (pendingImages.length === 0) return html;

  let result = html;
  pendingImages.forEach((img) => {
    const token = img.getAttribute("data-pending-token");
    const src = img.getAttribute("src") || "";
    if (token && src.startsWith("blob:") && result.includes(src)) {
      result = result.split(src).join(`pending:${token}`);
    }
  });
  return result;
};

// Reverses transformPendingImageMarkers for display: rebuilds a live blob: URL for any
// pending:{token} marker by looking the file up in pendingImagesForField, so RichPreview and
// a freshly-mounted Editor can actually render a not-yet-saved image.
const rehydratePendingImageSrcs = (html, pendingImagesForField, blobUrlCache) => {
  if (!html || !pendingImagesForField.length || !html.includes("pending:")) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  let changed = false;
  doc.querySelectorAll('img[src^="pending:"]').forEach((img) => {
    const src = img.getAttribute("src") || "";
    const token = src.slice("pending:".length);
    const pending = pendingImagesForField.find((p) => p.placeholderToken === token);
    if (!pending?.file) return;
    let blobUrl = blobUrlCache.get(token);
    if (!blobUrl) {
      blobUrl = URL.createObjectURL(pending.file);
      blobUrlCache.set(token, blobUrl);
    }
    img.setAttribute("src", blobUrl);
    changed = true;
  });
  return changed ? doc.body.innerHTML : html;
};

// ASTT-7319 follow-up: rehydrates an ALREADY-SAVED inline image's src --
// distinct from rehydratePendingImageSrcs above, which only handles
// not-yet-saved images still tracked in pendingImagesForField. A saved
// image's src is the real SharePoint URL, which a plain <img> can never
// load directly (SharePoint uploads use certificate-based, app-only auth
// held server-side only -- confirmed via a 401 reproduced in non-prod).
// downloadImageAsBlobUrl is injected (rather than importing the service
// call directly) so this file stays decoupled from the service layer,
// matching how the rest of this component works; the caller supplies
// downloadAttachmentImageAsBlobUrl from learnPointUserServices.js.
// blobUrlCache is keyed by the SharePoint URL itself (not a placeholder
// token, since there's no token here) and is expected to be a ref the
// caller owns and revokes on unmount, same lifecycle as
// rehydratePendingImageSrcs's own cache.
export const rehydrateSavedImageSrcs = async (
  html,
  attachments,
  downloadImageAsBlobUrl,
  blobUrlCache,
) => {
  if (!html || !attachments?.length) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const images = Array.from(doc.querySelectorAll('img[src^="http"]'));
  if (images.length === 0) return html;

  let changed = false;
  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute("src") || "";
      const attachment = attachments.find((a) => a.FileUrl === src);
      // Not a tracked inline image (e.g. some other external <img> a user
      // pasted in as plain HTML) -- leave it exactly as-is.
      if (!attachment) return;

      let blobUrl = blobUrlCache.get(src);
      if (!blobUrl) {
        try {
          blobUrl = await downloadImageAsBlobUrl(attachment.FileDocLibId);
          blobUrlCache.set(src, blobUrl);
        } catch (err) {
          // Leave the original (broken) src rather than throwing --
          // one failed image shouldn't block the rest of the field
          // from rendering.
          return;
        }
      }
      img.setAttribute("src", blobUrl);
      changed = true;
    }),
  );
  return changed ? doc.body.innerHTML : html;
};

export const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

// Per-field inline-image count, shown next to this field's own character counter.
export const countImagesInHtml = (html) => {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelectorAll("img").length;
};

// Adds inline styles to table markup so it survives Outlook (ignores <style> blocks/classes).
// Purely additive on table elements — must never strip a class quill-table-better relies on
// to reconstruct its table blot on next load (see sanitizeEditorHtml below).
const applyTableEmailStyles = (doc) => {
  doc.querySelectorAll("table").forEach((table) => {
    if (!table.getAttribute("style")) {
      table.setAttribute(
        "style",
        "border-collapse:collapse;width:100%;margin:8px 0;",
      );
    }
  });

  doc.querySelectorAll("td, th").forEach((cell) => {
    const existingStyle = cell.getAttribute("style") || "";
    if (!existingStyle.includes("border")) {
      const merged = `${existingStyle};border:1px solid #ccc;padding:6px 8px;vertical-align:top;`
        .replace(/^;/, "");
      cell.setAttribute("style", merged);
    }
  });
};

// Same idea as applyTableEmailStyles, for pasted images — adds inline max-width/height so a
// screenshot can't blow out the email layout. Merges rather than skips so a
// blot-formatter2 resize's own inline styles are preserved.
const applyImageEmailStyles = (doc) => {
  doc.querySelectorAll("img").forEach((img) => {
    const existingStyle = img.getAttribute("style") || "";
    const hasMaxWidth = /max-width\s*:/.test(existingStyle);
    const hasHeight =
      /(^|;)\s*height\s*:/.test(existingStyle) || img.hasAttribute("height");

    let merged = existingStyle;
    if (!hasMaxWidth) merged = `${merged};max-width:100%;`;
    if (!hasHeight) merged = `${merged};height:auto;`;
    merged = merged.replace(/^;+/, "").replace(/;{2,}/g, ";");

    if (merged !== existingStyle) {
      img.setAttribute("style", merged);
    }
  });
};

// Sanitizes saved html: normalizes Quill's list markup, strips ql-* classes (except table and
// image/iframe alignment classes, which must survive for content to reload correctly), and
// applies email-safe inline styles for tables/images.
export const sanitizeEditorHtml = (html) => {
  if (!html || html === "<p><br></p>") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Convert Quill's data-list="bullet" / data-list="ordered" to standard <ul>/<ol>
  const listItems = doc.querySelectorAll("li[data-list]");
  listItems.forEach((li) => {
    // Remove ql-ui spans (Quill internal UI elements)
    li.querySelectorAll(".ql-ui").forEach((el) => el.remove());
    // Remove Quill-specific attributes
    li.removeAttribute("data-list");
    li.removeAttribute("contenteditable");
  });

  // Remove contenteditable from any remaining elements
  doc.querySelectorAll("[contenteditable]").forEach((el) => {
    el.removeAttribute("contenteditable");
  });

  // Table cleanup must run before the ql-* class strip below (which skips table content).
  applyTableEmailStyles(doc);

  applyImageEmailStyles(doc);

  // Strip ql-* classes except table-better's own and image/iframe alignment classes —
  // stripping those would silently break reload (table) or alignment persistence (image).
  doc.querySelectorAll("[class]").forEach((el) => {
    if (el.closest("table")) return;
    const classes = [...el.classList].filter(
      (c) =>
        c.startsWith("ql-") &&
        !c.startsWith("ql-image-align-") &&
        !c.startsWith("ql-iframe-align-"),
    );
    classes.forEach((c) => el.classList.remove(c));
    if (el.classList.length === 0) el.removeAttribute("class");
  });

  return doc.body.innerHTML;
};

// ASTT-7319 follow-up: reverted back to this component after confirming
// the "always visible" ribbon (which removed this) caused genuine
// regressions -- existing content not appearing on load, and read-only
// entries still accepting typed input. Both stemmed from the editor
// being permanently mounted in a "frozen" state from its very first
// render, before any async fetch of existing data had a chance to
// complete. This preview, by contrast, reads the html prop reactively on
// every render -- there's no freezing to go stale in the first place,
// which is exactly why it was safe before and needs to come back now.
//
// minHeight added (was a fixed height: "160px") specifically to reduce
// the height-jump/flicker between this and the active editor -- the
// active editor's total height (content area + toolbar) is taller than
// this preview alone, so matching this preview's minHeight to that same
// total means the surrounding layout doesn't visibly shift when a field
// is clicked into or blurred, even though the actual DOM node being
// swapped is still a completely different one (RichPreview vs Editor,
// not the same element resizing) -- a CSS transition can't bridge that
// swap, but holding both states to a consistent minimum height removes
// most of the visible jump anyway.
const RichPreview = ({ html = "", label, onClick = undefined, disabled = false }) => (
  <Box
    className={styles.richTextEditorRoot}
    onClick={disabled ? undefined : onClick}
    sx={{
      minHeight: "210px",
      overflowY: "auto",
      border: "1px solid #d3d3d3",
      borderRadius: "4px",
      padding: "10px 14px",
      cursor: disabled ? "default" : "pointer",
      backgroundColor: disabled ? "#f5f5f5" : "#fff",
      "&:hover": disabled
        ? {}
        : {
            borderColor: "#3299FF",
            boxShadow: "0 0 0 1px #3299FF",
          },
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: html && html !== "<p><br></p>" ? "inherit" : "#aaa",
      "& ul, & ol": { paddingLeft: "1.5rem" },
      // Fallback table styling for the in-session preview (before sanitizeEditorHtml runs).
      "& table": { borderCollapse: "collapse", width: "100%" },
      "& td, & th": { border: "1px solid #ccc", padding: "4px 8px" },
      // Constrains a pasted screenshot so it can't overflow this preview box.
      "& img": { maxWidth: "100%", height: "auto" },
    }}
  >
    {html && html !== "<p><br></p>" ? (
      <span dangerouslySetInnerHTML={{ __html: html }} />
    ) : (
      `Click to add ${label}...`
    )}
  </Box>
);

RichPreview.propTypes = {
  html: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

const tableBetterModuleConfig = {
  language: "en_US",
  menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
  toolbarTable: true,
};

const RichTextField = ({
  label,
  value = "",
  onChange,
  isActive,
  onActivate,
  onDeactivate,
  isReadOnly,
  charCount,
  maxLength,
  onAIClick,
  aiColor,
  editorKey = 0,
  fieldName = undefined,
  onImagePending = undefined,
  pendingImages = [],
  // ASTT-7319 follow-up: saved-image rehydration, per direct request --
  // existingAttachments/downloadImageAsBlobUrl are injected the same way
  // onImagePending's own upload path is, so this file stays decoupled
  // from the service layer.
  existingAttachments = [],
  downloadImageAsBlobUrl = undefined,
}) => {
  const containerRef = useRef(null);
  // Guards the corrective reload in handleEditorLoad from bubbling to parent onChange.
  const isReloadingRef = useRef(false);
  const [pasteError, setPasteError] = useState("");

  // Captured so handleTextChange can query the live quill DOM directly.
  const quillRef = useRef(null);

  // This field's own slice of the parent's pendingImages, used to rehydrate pending:{token}
  // markers back into renderable blob: URLs wherever value is displayed, not actively edited.
  const pendingImagesForField = pendingImages.filter((p) => p.fieldName === fieldName);
  const blobUrlCacheRef = useRef(new Map());
  const displayValue = rehydratePendingImageSrcs(
    value,
    pendingImagesForField,
    blobUrlCacheRef.current,
  );

  // Revoke cached rehydration blob URLs on unmount.
  useEffect(() => {
    const cache = blobUrlCacheRef.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  // ASTT-7319 follow-up: rehydrates ALREADY-SAVED inline images' src to a
  // fresh, authenticated blob: URL -- distinct from pendingImagesForField
  // above, which only covers not-yet-saved images. A saved image's src is
  // the real SharePoint URL, which a plain <img> can never load directly
  // (SharePoint uploads use certificate-based, app-only auth held
  // server-side only; confirmed via a 401 reproduced in non-prod).
  //
  // Deliberately computed into its OWN internal state (fullyRehydratedValue),
  // never written back to the parent's value/onChange -- this needs to be
  // async (fetching bytes over the network), unlike rehydratePendingImageSrcs's
  // synchronous, render-time displayValue above, so it can't just be a
  // plain const the way that one is. An EARLIER version of this fix
  // mistakenly called the parent's own setSuccesses/setChallenges/etc.
  // from a similar effect in TrainingNotes.js/HandOffReport.js directly --
  // which meant the very next Save/Send resubmitted that transient blob:
  // URL as the new saved content, permanently overwriting the real
  // SharePoint URL in the database and silently dropping the image from
  // outgoing emails (EmbedInlineImagesAsync could no longer find the real
  // FileUrl to substitute). Keeping this local to display state only,
  // exactly mirroring how pendingImagesForField/displayValue never
  // mutate the parent either, is what avoids that recurring.
  const savedImageBlobUrlCacheRef = useRef(new Map());
  const [fullyRehydratedValue, setFullyRehydratedValue] = useState(displayValue);
  useEffect(() => {
    let cancelled = false;

    const rehydrateSaved = async () => {
      if (existingAttachments.length === 0 || !downloadImageAsBlobUrl) {
        if (!cancelled) setFullyRehydratedValue(displayValue);
        return;
      }
      const result = await rehydrateSavedImageSrcs(
        displayValue,
        existingAttachments,
        downloadImageAsBlobUrl,
        savedImageBlobUrlCacheRef.current,
      );
      if (!cancelled) setFullyRehydratedValue(result);
    };

    rehydrateSaved();
    return () => {
      cancelled = true;
    };
  }, [displayValue, existingAttachments, downloadImageAsBlobUrl]);

  // Revoke cached saved-image rehydration blob URLs on unmount.
  useEffect(() => {
    const cache = savedImageBlobUrlCacheRef.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  // PrimeReact's <Editor> internally reconverts content from its `value` prop whenever that
  // prop changes while Quill.hasFocus() reads false (a focus-timing race around native paste
  // events) — which doesn't preserve our data-pending-token attribute and can corrupt an
  // in-flight image insert. Fix: freeze the value fed to <Editor>, only refreshing it on a
  // genuine remount (activation or editorKey change), never as a side effect of mirroring the
  // editor's own edits back to itself.
  const wasActiveRef = useRef(false);
  const lastEditorKeyRef = useRef(editorKey);
  const frozenEditorValueRef = useRef(fullyRehydratedValue);
  const justActivated = isActive && !wasActiveRef.current;
  const editorKeyChanged = editorKey !== lastEditorKeyRef.current;
  if (justActivated || editorKeyChanged) {
    frozenEditorValueRef.current = fullyRehydratedValue;
  }
  wasActiveRef.current = isActive;
  lastEditorKeyRef.current = editorKey;

  // Stashes the actual pasted file(s) so the img matcher (handleEditorLoad below) can
  // substitute a real blob: URL for whatever the HTML-fallback paste path provides instead.
  // Capture-phase on `document` (a genuine ancestor of quill.root) runs before Quill's own
  // bubble-phase listener, regardless of attachment order.
  const pendingClipboardFilesRef = useRef([]);
  useEffect(() => {
    const handleDocumentPasteCapture = (event) => {
      if (!containerRef.current?.contains(event.target)) return;

      // ASTT-7319 follow-up: the earlier "block image paste entirely"
      // logic removed -- confirmed the actual root cause (a save-flow
      // race condition clearing pendingImages/deactivating the field
      // before the server's corrected, real-URL content had finished
      // reloading) is now fixed, and that fix applies identically
      // regardless of how an image was inserted, paste or the ribbon
      // button -- there was never anything paste-specific about the bug
      // itself once traced to its actual source.
      const files = Array.from(event.clipboardData?.files || []);
      if (files.length > 0) {
        pendingClipboardFilesRef.current = files;
      }
    };
    document.addEventListener("paste", handleDocumentPasteCapture, true);
    return () => document.removeEventListener("paste", handleDocumentPasteCapture, true);
  }, []);

  // Images inserted via the img matcher are queued here (by blob: URL) until the next
  // text-change confirms the node exists in the DOM and can be tagged/dispatched.
  const pendingImageMetaQueueRef = useRef([]);

  // Maximize/dock into a modal for a roomier workspace — same value/onChange/Quill session.
  const [isMaximized, setIsMaximized] = useState(false);

  const imageCountInField = useMemo(() => countImagesInHtml(value), [value]);

  // Replaces Quill's default Uploader handler (base64 data:, untracked) with ours (blob: URL,
  // tagged, tracked via onImagePending). Reassigned every render via ref so the `modules`
  // config's stable wrapper always calls the latest fieldName/onImagePending closure.
  const uploadHandlerRef = useRef(null);
  uploadHandlerRef.current = (range, files) => {
    const quill = quillRef.current;
    if (!quill) return;
    setPasteError("");

    files.forEach((rawFile) => {
      // ASTT-7294 follow-up (robustness): the paste path validates MIME type before size;
      // this toolbar path was only checking size — an unsupported type would preview fine
      // and only fail once the user hit Save, with a less specific error than this gives.
      if (!ALLOWED_INLINE_IMAGE_MIME_TYPES.includes(rawFile.type)) {
        setPasteError(
          `'${rawFile.name}' is not a supported image type (PNG, JPEG, or GIF only).`,
        );
        return;
      }
      if (rawFile.size > MAX_INLINE_IMAGE_SIZE_BYTES) {
        setPasteError("Pasted image exceeds the 2MB attachment limit.");
        return;
      }

      const file = makeUniqueImageFile(rawFile);
      const placeholderToken = makePlaceholderToken();
      const blobUrl = URL.createObjectURL(file);

      // ASTT-7294 follow-up (robustness): queue BEFORE insertEmbed, not after. insertEmbed
      // with source USER fires Quill's text-change event synchronously, before this
      // function's next line runs — tagging the node here (after insertEmbed) meant
      // handleTextChange already ran once with no tag present, sending the raw blob: URL to
      // onChange instead of a pending:{token} marker. That blob: URL never survives to Save,
      // which is why toolbar-inserted images displayed fine but reverted to a broken glyph
      // after saving. Routing through the same queue the paste path already uses lets
      // handleTextChange's existing drain logic (which runs before transformPendingImageMarkers
      // on the very same event) tag the node in time, exactly like paste already does.
      pendingImageMetaQueueRef.current.push({ file, placeholderToken, blobUrl, fieldName });

      quill.insertEmbed(range.index, "image", blobUrl, Quill.sources.USER);
      quill.setSelection(range.index + 1, 0, Quill.sources.SILENT);
    });
  };

  useEffect(() => {
    if (!isActive) return;
    const handleMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onDeactivate();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isActive, onDeactivate]);

  // ASTT-7319 follow-up: moved up from below renderEditor -- toolbarTemplate
  // (which references handleMaximizeClick) is defined right after this,
  // and both need to exist before that reference, not after. Leaving them
  // declared after toolbarTemplate (their original position, harmless
  // there before this session's edit since nothing above referenced them
  // yet) caused a genuine "Cannot access before initialization" crash the
  // moment toolbarTemplate's own definition tried to read
  // handleMaximizeClick out of the temporal dead zone.
  const handleMaximizeClick = () => {
    if (!isActive) onActivate();
    // ASTT-7294 follow-up: the Dialog's Editor is a brand-new mount (MUI Dialog portals to
    // document.body — it can never be the same <Editor> instance as the inline one), and
    // every fresh mount seeds itself from frozenEditorValueRef, which is deliberately NOT
    // kept in sync with every keystroke (see its comment above — that's the fix for the
    // table-persistence bug). Without this explicit resync, anything typed or pasted since
    // this field was first activated is invisible to the newly-mounted dialog editor —
    // this is the "sometimes blank on maximize" bug. fullyRehydratedValue here is this
    // render's freshest known value, computed from the parent's up-to-date state.
    frozenEditorValueRef.current = fullyRehydratedValue;
    setIsMaximized(true);
  };

  const handleRestore = () => {
    // Mirrors handleMaximizeClick's resync for the reverse transition — the inline slot
    // remounts fresh too, with the exact same staleness risk.
    frozenEditorValueRef.current = fullyRehydratedValue;
    setIsMaximized(false);
  };

  // ASTT-7319 follow-up: Maximize/AI Writing Assistant moved here from the
  // separate label row above, per direct request -- both now sit inside
  // this editor's own menu bar/toolbar, at the end. Wrapped in a flex
  // container alongside Quill's native ql-formats group (not merged into
  // that same span) so Quill's toolbar module, which scans strictly for
  // ql-* classed elements, has no reason to treat these two custom,
  // non-Quill buttons any differently -- they keep their own onClick
  // handlers exactly as before, just relocated visually.
  const toolbarTemplate = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span className="ql-formats">
        <select className="ql-header" defaultValue="">
          <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="">Normal</option>
      </select>
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
      <button className="ql-strike" />
      <button className="ql-list" value="ordered" />
      <button className="ql-list" value="bullet" />
      <button className="ql-indent" value="-1" />
      <button className="ql-indent" value="+1" />
      <select className="ql-color" />
      <select className="ql-background" />
      <button className="ql-blockquote" />
      {/* Inserts/edits a table (quill-table-better). */}
      <button className="ql-table-better" aria-label="Insert table" />
      {/* File-picker image insert, bypassing clipboard/paste entirely — handler registered
          via Toolbar.addHandler("image", ...) in handleEditorLoad below. */}
      <button className="ql-image" aria-label="Insert image" />
      <button className="ql-clean" />
      </span>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {/* ASTT-7319 follow-up: !isMaximized added -- once maximized, the
            Dialog itself already has its own minimize/restore control,
            so this button showing too was redundant (and confusingly,
            it's the SAME onClick target that opens the dialog in the
            first place -- clicking it again while already maximized had
            no defined behavior). Only rendered in the minimized state
            now. */}
        {!isReadOnly && !isMaximized && (
          // ASTT-7319: was a bare icon with onClick -- not keyboard
          // focusable/operable, and Tooltip's title becomes
          // aria-describedby (a supplementary hint), not a real
          // accessible name. Wrapping in a real IconButton with an
          // explicit aria-label fixes both; the Tooltip stays for the
          // visual hover hint, it's complementary, not a replacement.
          <Tooltip title="Maximize editor" arrow>
            <IconButton
              aria-label="Maximize editor"
              size="small"
              onClick={handleMaximizeClick}
              sx={{ padding: 0 }}
            >
              <FullscreenIcon
                sx={{
                  fontSize: 20,
                  // ASTT-7319 follow-up: was #777 (a dark, near-black
                  // gray) -- confirmed via direct feedback it read as
                  // black, unlike AI Writing Assistant's own icon in this
                  // same ribbon. Matched to the app's established primary
                  // blue instead, the same color aiColor defaults to.
                  color: "#3299FF",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="AI Writing Assistant" arrow>
          <IconButton
            aria-label="AI Writing Assistant"
            size="small"
            onClick={onAIClick}
            disabled={charCount < 20 || isReadOnly}
            sx={{
              padding: 0,
            }}
          >
            <AutoAwesomeIcon
              sx={{
                fontSize: 22,
                // ASTT-7319 follow-up: was visibility: hidden below 20
                // characters -- per direct request, now stays visible
                // throughout, just greyed out/faded until the length
                // threshold is met, rather than disappearing entirely.
                // aiColor itself is only ever passed as the app's primary
                // blue by every caller, so this doesn't need its own
                // separate disabled-color prop -- a fixed grey covers it.
                color: charCount >= 20 && !isReadOnly ? aiColor : "#bdbdbd",
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  // 1. Diagnostic: confirms quill-table-better attached to this instance.
  // 2. Reloads content via updateContents (table-safe) after PrimeReact's own table-breaking
  //    setContents-based initial load; isReloadingRef stops this reaching parent state.
  const handleEditorLoad = (quill) => {
    quillRef.current = quill;

    try {
      const tableModule = quill?.getModule?.("table-better");
      if (!tableModule) {
        // eslint-disable-next-line no-console
        console.warn(
          "[RichTextEditorField] quill-table-better module not found on this Quill instance.",
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[RichTextEditorField] onLoad diagnostic check failed:", err);
    }

    // Registers the image toolbar button's file-picker handler. Done here (not via
    // modules.toolbar.handlers) because PrimeReact builds Quill's toolbar config as
    // `{ toolbar: toolbarRef.current, ...props.modules }` — a toolbar key in props.modules
    // would replace toolbarRef.current entirely and break every toolbar button.
    const toolbarModule = quill?.getModule?.("toolbar");
    if (toolbarModule && !quill.__dtrImageHandlerAttached) {
      toolbarModule.addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", ALLOWED_INLINE_IMAGE_MIME_TYPES.join(","));
        input.addEventListener("change", () => {
          const range = quill.getSelection(true);
          const files = Array.from(input.files || []);
          if (files.length > 0) {
            uploadHandlerRef.current?.(range, files);
          }
        });
        input.click();
      });
      quill.__dtrImageHandlerAttached = true;
    }

    // Catches the HTML-fallback paste path (see uploader.handler in the modules prop below
    // for the files-only path) by substituting a real blob: URL for whatever src the pasted
    // HTML itself specifies.
    if (quill && !quill.__dtrImgMatcherAttached) {
      quill.clipboard.addMatcher("img", (node, delta) => {
        // clipboard.convert() (used by handleEditorLoad's table-safe content reload below)
        // runs through this SAME matcher for every <img> in already-saved content — guard
        // against re-queuing an already-uploaded image for "upload" on every mount/reload.
        if (isReloadingRef.current) return delta;

        const files = pendingClipboardFilesRef.current;
        const rawFile = files && files.length > 0 ? files.shift() : null;

        if (rawFile) {
          if (!ALLOWED_INLINE_IMAGE_MIME_TYPES.includes(rawFile.type)) return delta;
          if (rawFile.size > MAX_INLINE_IMAGE_SIZE_BYTES) {
            setPasteError("Pasted image exceeds the 2MB attachment limit.");
            return new QuillDelta();
          }

          const file = makeUniqueImageFile(rawFile);
          const blobUrl = URL.createObjectURL(file);
          const placeholderToken = makePlaceholderToken();
          pendingImageMetaQueueRef.current.push({ file, placeholderToken, blobUrl, fieldName });
          return new QuillDelta().insert({ image: blobUrl });
        }

        // ASTT-7294 follow-up (robustness): clipboardData.files is only populated for some
        // paste sources (screenshots, files copied from Explorer). Many others — copying an
        // image directly off a web page, some Office paste paths — put only HTML with a
        // data: URI on the clipboard, never a File. Without this fallback those images
        // render fine (a data: URI is self-contained) but silently never reach
        // onImagePending, so they're never uploaded to SharePoint — reported as
        // "intermittent" because it tracks the source app, not anything nondeterministic
        // in this component.
        const srcAttr = node.getAttribute?.("src") || "";
        const dataUriMatch = /^data:image\/(png|jpe?g|gif);base64,(.+)$/i.exec(srcAttr);
        if (dataUriMatch) {
          const mimeType = `image/${dataUriMatch[1].toLowerCase() === "jpg" ? "jpeg" : dataUriMatch[1].toLowerCase()}`;
          if (!ALLOWED_INLINE_IMAGE_MIME_TYPES.includes(mimeType)) return delta;

          let bytes;
          try {
            const binary = atob(dataUriMatch[2]);
            bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[RichTextEditorField] Failed to decode pasted data: URI image:", err);
            return delta;
          }

          if (bytes.length > MAX_INLINE_IMAGE_SIZE_BYTES) {
            setPasteError("Pasted image exceeds the 2MB attachment limit.");
            return new QuillDelta();
          }

          const file = makeUniqueImageFile(
            new File([bytes], "pasted-image", { type: mimeType }),
          );
          const blobUrl = URL.createObjectURL(file);
          const placeholderToken = makePlaceholderToken();
          pendingImageMetaQueueRef.current.push({ file, placeholderToken, blobUrl, fieldName });
          return new QuillDelta().insert({ image: blobUrl });
        }

        // Neither a captured File nor a decodable data: URI — most likely a remote http(s)
        // image reference (e.g. pasted as a live link rather than image data). Quill's
        // sanitizer will still let it render as-is, but it was never captured as a File and
        // will never be uploaded to SharePoint. Surface that explicitly rather than let it
        // look like a normal, tracked, soon-to-be-saved image.
        if (srcAttr && !srcAttr.startsWith("blob:")) {
          setPasteError(
            "This image couldn't be captured for upload (unsupported paste source) and won't be saved with the report.",
          );
        }
        return delta;
      });
      quill.__dtrImgMatcherAttached = true;
    }

    if (!quill || !value || value === "<p><br></p>") return;

    try {
      isReloadingRef.current = true;
      const delta = quill.clipboard.convert({ html: frozenEditorValueRef.current, text: "\n" });
      quill.setText("", Quill.sources.SILENT);
      quill.updateContents(delta, Quill.sources.USER);
      quill.setSelection(0, Quill.sources.SILENT);
      quill.history?.clear?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[RichTextEditorField] table-safe content reload failed on mount:",
        err,
      );
    } finally {
      isReloadingRef.current = false;
    }
  };

  const handleTextChange = (e) => {
    if (isReloadingRef.current) return;
    const quill = quillRef.current;

    // Tags + dispatches images inserted via the img matcher, once their DOM node exists.
    if (quill && pendingImageMetaQueueRef.current.length > 0) {
      const stillPending = [];
      pendingImageMetaQueueRef.current.forEach((meta) => {
        const img = quill.root.querySelector(
          `img[src="${meta.blobUrl}"]:not([data-pending-token])`,
        );
        if (img) {
          img.setAttribute("data-pending-token", meta.placeholderToken);
          onImagePending?.(meta.file, {
            placeholderToken: meta.placeholderToken,
            fieldName: meta.fieldName,
          });
        } else {
          stillPending.push(meta);
        }
      });
      pendingImageMetaQueueRef.current = stillPending;
    }

    const transformedHtml = transformPendingImageMarkers(e.htmlValue, quill);
    onChange(
      transformedHtml === e.htmlValue ? e : { ...e, htmlValue: transformedHtml },
    );
  };

  // ASTT-7319 follow-up: handleMaximizeClick/handleRestore moved above,
  // right before toolbarTemplate (which references handleMaximizeClick) --
  // see the comment there for why.

  // Shared between the inline slot and the modal so they can never drift out of sync.
  const renderEditor = (height) => (
    <Editor
      key={editorKey}
      className={styles.richTextEditorRoot}
      // Frozen per activation/editorKey change — see frozenEditorValueRef's comment above.
      value={frozenEditorValueRef.current}
      onTextChange={handleTextChange}
      headerTemplate={toolbarTemplate}
      modules={{
        table: false,
        "table-better": tableBetterModuleConfig,
        // Replaces Quill's default base64 Uploader handler with uploadHandlerRef (blob: URL,
        // tagged, tracked). mimetypes repeated here so a GIF isn't dropped by Quill's default
        // png/jpeg-only filter before our handler sees it.
        uploader: {
          mimetypes: ALLOWED_INLINE_IMAGE_MIME_TYPES,
          handler(range, files) {
            uploadHandlerRef.current?.(range, files);
          },
        },
        // Resize handles + on-select toolbar (align/delete/alt-title) for images.
        blotFormatter2: {},
        keyboard: {
          bindings: QuillTableBetter.keyboardBindings,
        },
      }}
      onLoad={handleEditorLoad}
      autoFocus
      style={{ height }}
    />
  );

  const metricsFooter = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mt: 0.5,
      }}
    >
      <Box component="span" sx={{ fontSize: "0.75rem", color: "#777" }}>
        {imageCountInField > 0
          ? `${imageCountInField} image${imageCountInField === 1 ? "" : "s"} in this field`
          : ""}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: "0.75rem",
          color: charCount >= maxLength ? "red" : "inherit",
        }}
      >
        {`${charCount}/${maxLength}`}
      </Box>
    </Box>
  );

  return (
    <Box ref={containerRef}>
      {/* ASTT-7319 follow-up: Maximize/AI Writing Assistant moved into
          the editor's own toolbar (toolbarTemplate below), per direct
          request -- this row is now just the label, no longer a flex
          row needing justifyContent: "space-between" for the icons that
          used to share it. */}
      <Box sx={{ mb: 0.5 }}>
        <Box
          component="span"
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#555",
            letterSpacing: "0.04em",
          }}
        >
          {label}
        </Box>
      </Box>

      {isMaximized ? (
        // MUI Dialog portals to document.body, so the same Editor can't render in both
        // places — a fresh instance mounts in the Dialog each time. handleMaximizeClick /
        // handleRestore explicitly resync frozenEditorValueRef immediately before this
        // transition so that fresh mount starts from current content, not a stale snapshot
        // (see those handlers' comments — content is NOT automatically safe here, despite
        // what an earlier version of this comment claimed).
        <Box
          onClick={handleMaximizeClick}
          sx={{
            height: "160px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #b0bec5",
            borderRadius: "4px",
            backgroundColor: "#fafafa",
            color: "#777",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          Editing in full-screen — click to return here
        </Box>
      ) : isActive ? (
        // ASTT-7319 follow-up: reverted back to the isActive-gated
        // ternary (was calling renderEditor unconditionally) -- the
        // "always visible" toolbar, per direct request, turned out not
        // to be feasible: it caused existing content not appearing on
        // load and read-only entries still accepting input, both traced
        // to the editor being permanently frozen from its very first
        // render rather than only once actually clicked into. Confirmed
        // stable and preferred: read-only preview until clicked, full
        // editor with ribbon after.
        renderEditor("160px")
      ) : (
        <RichPreview
          html={fullyRehydratedValue}
          label={label}
          onClick={onActivate}
          disabled={isReadOnly}
        />
      )}

      {metricsFooter}
      {pasteError && (
        <Box
          component="span"
          sx={{
            fontSize: "0.75rem",
            color: "red",
            display: "block",
            mt: 0.5,
          }}
        >
          {pasteError}
        </Box>
      )}

      <Dialog
        open={isMaximized}
        onClose={handleRestore}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {label}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Restore" arrow>
              <IconButton
                aria-label="Restore to normal size"
                size="small"
                onClick={handleRestore}
              >
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* ASTT-7319: this Close button had no Tooltip and no
                aria-label at all -- zero accessible name of any kind. */}
            <Tooltip title="Close" arrow>
              <IconButton
                aria-label="Close"
                size="small"
                onClick={handleRestore}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isMaximized && renderEditor("60vh")}
          {metricsFooter}
          {pasteError && (
            <Box
              component="span"
              sx={{
                fontSize: "0.75rem",
                color: "red",
                display: "block",
                mt: 0.5,
              }}
            >
              {pasteError}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

RichTextField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  onActivate: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  charCount: PropTypes.number.isRequired,
  maxLength: PropTypes.number.isRequired,
  onAIClick: PropTypes.func.isRequired,
  aiColor: PropTypes.string.isRequired,
  editorKey: PropTypes.number,
  // Identifies which text field this instance edits (included in onImagePending metadata).
  fieldName: PropTypes.string,
  // Fired the moment an image is pasted/inserted (local blob: preview already shown) — the
  // parent should hold onto `file` until Save, then include it in the combined-save request.
  onImagePending: PropTypes.func,
  // Parent's full pendingImages list (filtered internally to this field) — used to rehydrate
  // pending:{token} markers back into live blob: URLs wherever value isn't actively edited.
  pendingImages: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.instanceOf(File),
      placeholderToken: PropTypes.string,
      fieldName: PropTypes.string,
    }),
  ),
};

export default RichTextField;
